import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from './Navigation';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {LanguageSelector} from './LanguageSelector';
import {Logo} from "../../../base/Logo";

export const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-lg shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link href="#hero">
            <Logo isWhite={!isScrolled}
                    textColorClassName={isScrolled ? 'text-gray-900' : 'text-white'}
                    onClick={() => setIsMenuOpen(false)}
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link href="#features" className={isScrolled ? 'text-gray-600' : 'text-white'}>{t('header.nav.features')}</Link>
            <button
                onClick={() => handleNavigation('/info/services')}
                className={`font-medium transition-colors ${
                    isScrolled
                        ? 'text-gray-600 hover:text-indigo-600'
                        : 'text-white hover:text-indigo-600'
                }`}
            >
              {t('header.nav.services')}
            </button>
            <Link href="#how-it-works" className={isScrolled ? 'text-gray-600' : 'text-white'}>{t('header.nav.howItWorks')}</Link>
            <Link href="#pricing" className={isScrolled ? 'text-gray-600' : 'text-white'}>{t('header.nav.pricing')}</Link>
            <Link href="#contact" className={isScrolled ? 'text-gray-600' : 'text-white'}>{t('header.nav.contact')}</Link>
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <LanguageSelector isScrolled={isScrolled} />
            <button
              onClick={() => handleNavigation('/login')}
              className={`px-4 py-2 rounded-full font-medium transition-all ${
                isScrolled
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-white text-indigo-600 hover:bg-indigo-50'
              }`}
            >
              {t('header.actions.logIn')}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <LanguageSelector isScrolled={isScrolled} />
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className={`h-6 w-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
              ) : (
                <Menu className={`h-6 w-6 ${isScrolled ? 'text-gray-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-lg">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <a 
              href="#features" 
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('header.nav.features')}
            </a>
            <a 
              href="#how-it-works" 
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('header.nav.howItWorks')}
            </a>
            <a 
              href="#pricing" 
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('header.nav.pricing')}
            </a>
            <a 
              href="#contact" 
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              {t('header.nav.contact')}
            </a>
            <div className="pt-4 pb-3 border-t border-gray-200">
              <button
                onClick={() => handleNavigation('/login')}
                className="mt-1 block w-full text-left px-3 py-2 rounded-md text-base font-medium bg-indigo-600 text-white hover:bg-indigo-700"
              >
                {t('header.actions.logIn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
